/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { store } from '../../core/store';
import {
    pricelistSchemaConnectorReducers,
    pricelistSchemaConstants,
    pricelistSchemaElementConstants,
    pricelistSchemaElementsReducers,
    pricelistSchemasReducers,
} from '../../modules/pricelists/reducers';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import CustomSmallTextButton from '../../components/Buttons/CustomSmallTextButton';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import PricelistSchemaList from './Components/PricelistSchemaList';
import PricelistSchemaElementList from './Components/PricelistSchemaElementList';
import PricelistSchemaEdit from './Components/PricelistSchemaEdit';
import { PricelistSchemaElementEdit } from './Components/PricelistSchemaElementEdit';
import { PricelistSchemaElementsCollectionInterface } from '../../interfaces/api/PricelistSchemaElementsCollectionInterface';
import { PricelistSchemasCollectionInterface } from '../../interfaces/api/PricelistSchemasCollectionInterface';
import { PricelistCloneDialog } from './Components/PricelistCloneDialog';
import { history } from '../../core/history';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';

store.injectReducer('pricelistSchemasReducers', pricelistSchemasReducers);
store.injectReducer('pricelistSchemaElementsReducers', pricelistSchemaElementsReducers);
store.injectReducer('pricelistSchemaConnectorReducers', pricelistSchemaConnectorReducers);

export const PricelistList = (): React.ReactElement => {
    const dispatch = useDispatch();
    const PRICELIST_SCHEMA_URI = '/pricelist_schemas';
    const PRICELIST_SCHEMA_ELEMENT_URI = '/pricelist_schema_elements';

    const [pricelistSchemaDialogOpen, setPricelistSchemaDialogOpen] = useState<boolean>(false);
    const [pricelistSchemaElementDialogOpen, setPricelistSchemaElementDialogOpen] = useState<boolean>(false);

    const [activePricelistSchema, setActivePricelistSchema] = useState<number | null>(null);
    const [activePricelistSchemaElement, setActivePricelistSchemaElement] = useState<number | null>(null);

    const [openCloneModal, setOpenCloneModal] = useState<boolean>(false);
    const [cloneParams, setCloneParams] = useState<{ id: number; type: string } | null>(null);

    const [schemaFilters, setSchemaFilters] = useState<Array<Filter>>([]);
    const [schemaOrders, setSchemaOrders] = useState<Array<Order>>([]);

    const [schemaElementFilters, setSchemaElementFilters] = useState<Array<Filter>>([]);
    const [schemaElementOrders, setSchemaElementOrders] = useState<Array<Order>>([]);

    const togglePricelistDialog = (): void => {
        setPricelistSchemaDialogOpen(!pricelistSchemaDialogOpen);
        if (pricelistSchemaDialogOpen) {
            setActivePricelistSchema(null);
        }
    };
    const togglePricelistElementDialog = (): void => {
        setPricelistSchemaElementDialogOpen(!pricelistSchemaElementDialogOpen);
        if (pricelistSchemaElementDialogOpen) {
            setActivePricelistSchemaElement(null);
        }
    };

    const editPricelist = (params: { pricelistId: number }): void => {
        setActivePricelistSchema(params.pricelistId);
        togglePricelistDialog();
    };

    const editPricelistElement = (params: { pricelistElementId: number }): void => {
        setActivePricelistSchemaElement(params.pricelistElementId);
        togglePricelistElementDialog();
    };

    const loadPricelistSchemaList = useCallback(
        (uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PRICELIST_SCHEMA_URI);
            }
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                pricelistSchemaConstants.LIST,
                [...schemaFilters, { name: 'deleted', value: false }],
                schemaOrders,
                (data: PricelistSchemasCollectionInterface) => {},
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: true,
                }
            );
        },
        [dispatch, schemaFilters, schemaOrders]
    );

    const loadPricelistSchemaElementList = useCallback(
        (uri?: string) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PRICELIST_SCHEMA_ELEMENT_URI);
            }
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                pricelistSchemaElementConstants.LIST,
                [...schemaElementFilters, { name: 'deleted', value: false }],
                schemaElementOrders,
                (data: PricelistSchemaElementsCollectionInterface) => {},
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: true,
                }
            );
        },
        [schemaElementFilters, schemaElementOrders, dispatch]
    );

    const toggleCloneModal = (): void => {
        setOpenCloneModal(!openCloneModal);
    };

    const clonePricelistHandler = (param: { id: number; type: string } | null): void => {
        toggleCloneModal();
        setCloneParams(param);
    };

    useEffect(() => {
        loadPricelistSchemaList();
    }, [loadPricelistSchemaList]);
    useEffect(() => {
        loadPricelistSchemaElementList();
    }, [loadPricelistSchemaElementList]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        if (history && history.location && history.location.state && history.location.state.pricelistSchemaElementId) {
            if (!pricelistSchemaElementDialogOpen) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                editPricelistElement({ pricelistElementId: history.location.state.pricelistSchemaElementId });
                history.location.state = null;
            }
        }
    });

    const updateSchemaOrderList = (orders: Array<Order>): void => {
        setSchemaOrders(orders);
    };
    const updateSchemaFilterList = (filters: Array<Filter>): void => {
        setSchemaFilters(filters);
    };

    const updateSchemaElementOrderList = (orders: Array<Order>): void => {
        setSchemaElementOrders(orders);
    };
    const updateSchemaElementFilterList = (filters: Array<Filter>): void => {
        setSchemaElementFilters(filters);
    };

    return (
        <>
            <GridContainer spacing={1}>
                <GridItem xs={12} md={12} lg={12} style={{ marginBottom: 20 }}>
                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PRICELIST_SCHEMA_POST) && (
                        <CustomTextButtom
                            color="primary"
                            label={'pricelists:b.add_pricelist'}
                            callback={togglePricelistDialog}
                            callbackParam={{}}
                            size={'lg'}
                        />
                    )}
                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PRICELIST_SCHEMA_ELEMENT_POST) && (
                        <CustomTextButtom
                            color="primary"
                            label={'pricelists:b.add_pricelist_element'}
                            callback={togglePricelistElementDialog}
                            callbackParam={{}}
                            size={'lg'}
                        />
                    )}
                </GridItem>
                <GridItem xs={12} md={6}>
                    <PricelistSchemaList
                        editPricelist={editPricelist}
                        loadPricelistSchemaList={loadPricelistSchemaList}
                        clonePricelistHandler={clonePricelistHandler}
                        updateSchemaOrderList={updateSchemaOrderList}
                        updateSchemaFilterList={updateSchemaFilterList}
                        schemaFilters={schemaFilters}
                        schemaOrders={schemaOrders}
                    />
                </GridItem>
                <GridItem xs={12} md={6}>
                    <PricelistSchemaElementList
                        clonePricelistHandler={clonePricelistHandler}
                        editPricelistElement={editPricelistElement}
                        loadPricelistSchemaElementList={loadPricelistSchemaElementList}
                        schemaElementFilters={schemaElementFilters}
                        setSchemaElementFilters={setSchemaElementFilters}
                        schemaElementOrders={schemaElementOrders}
                        setSchemaElementOrders={setSchemaElementOrders}
                    />
                </GridItem>
            </GridContainer>

            <PricelistSchemaEdit
                setActivePricelistSchema={setActivePricelistSchema}
                activePricelistSchema={activePricelistSchema}
                pricelistSchemaDialogOpen={pricelistSchemaDialogOpen}
                togglePricelistDialog={togglePricelistDialog}
                loadPricelistSchemaList={loadPricelistSchemaList}
                setPricelistSchemaDialogOpen={setPricelistSchemaDialogOpen}
            />
            <PricelistSchemaElementEdit
                setActivePricelistSchemaElement={setActivePricelistSchemaElement}
                activePricelistSchemaElement={activePricelistSchemaElement}
                pricelistSchemaElementDialogOpen={pricelistSchemaElementDialogOpen}
                togglePricelistElementDialog={togglePricelistElementDialog}
                loadPricelistSchemaElementList={loadPricelistSchemaElementList}
                setPricelistSchemaElementDialogOpen={setPricelistSchemaElementDialogOpen}
            />

            <PricelistCloneDialog
                clonePricelistHandler={clonePricelistHandler}
                openCloneModal={openCloneModal}
                cloneParams={cloneParams}
                loadPricelistSchemaList={loadPricelistSchemaList}
                loadPricelistSchemaElementList={loadPricelistSchemaElementList}
            />
        </>
    );
    // return <div>aaa</div>;
};
